import {logger} from "@/components/DownloaderApp/logger";
import confetti from 'canvas-confetti';
import {parse, types as hlsTypes} from "hls-parser";
import {ChooseFileLocationPopup} from "@/components/DownloaderApp/ChooseFileLocationPopup";
import {loadFFmpeg} from "@/components/DownloaderApp/ffmpeg-core";
import {Mp3ConversionError, type Mp3ConversionStage} from "@/components/DownloaderApp/download-errors";
import type {DownloadSaveInfo} from "@/components/DownloaderApp/types";
import {StreamSaverError} from "@/components/DownloaderApp/download-errors";

const MP3_INPUT_DIR = "/mounted";
const MP3_INPUT_FILE = "input.ts";
const MP3_OUTPUT_FILE = "output.mp3";
const MP3_LOG_TAIL_SIZE = 30;

export function errorMessageOf(err: unknown): string {
    if (err instanceof Error) return err.message;
    if (typeof err === "string") return err;
    if (err && typeof err === "object") {
        if ("message" in err && err.message) return String(err.message);
        if ("msg" in err && err.msg) return String(err.msg);

        try {
            return JSON.stringify(err) ?? String(err);
        } catch {
            return String(err);
        }
    }
    return String(err);
}

export async function toMp3(
    input?: ReadableStream<Uint8Array> | {readable: ReadableStream<Uint8Array>},
    onMessage = () => {}
) {
    const logTail: string[] = [];
    const fail = (
        stage: Mp3ConversionStage,
        description: string,
        options: {cause?: unknown; exitCode?: number} = {}
    ) => {
        const detail = options.cause !== undefined ? `: ${errorMessageOf(options.cause)}` : "";
        const error = new Mp3ConversionError(stage, `${description}${detail}`, {
            ...options,
            logTail: logTail.slice(),
        });
        logger.log(`toMp3 failed [${stage}]`, {message: error.message, logTail: error.logTail});
        return error;
    };

    let stream: ReadableStream<Uint8Array> | undefined =
        input && "readable" in input ? input.readable : input;

    if (!stream) {
        const response = await fetch(
            "https://stream.kick.com/3c81249a5ce0/ivs/v1/196233775518/gb20mPcO74wk/2026/1/4/18/55/uoKQPxCaLCVt/media/hls/720p60/3.ts"
        );
        const response2 = await fetch(
            "https://stream.kick.com/3c81249a5ce0/ivs/v1/196233775518/gb20mPcO74wk/2026/1/4/18/55/uoKQPxCaLCVt/media/hls/720p60/4.ts"
        );
        const writer = createReadableStream();

        writer.write(new Uint8Array(await response.arrayBuffer()));
        writer.write(new Uint8Array(await response2.arrayBuffer()));
        writer.close();
        stream = writer.readable;
    }

    // Keep the source out of the JS heap: a Blob is stored by the browser (and can be
    // spilled to disk) instead of living in the ~2GB 32-bit wasm/ArrayBuffer budget.
    let inputBlob: Blob;
    try {
        inputBlob = await readableStreamToBlob(stream, "video/mp2t");
    } catch (err) {
        throw fail("read-stream", "MP3 conversion failed while reading the downloaded video", {cause: err});
    }

    if (!inputBlob.size) {
        throw fail("read-stream", "MP3 conversion failed: the downloaded video is empty");
    }

    let ffmpeg: Awaited<ReturnType<typeof loadFFmpeg>>;
    try {
        ffmpeg = await loadFFmpeg(message => {
            logTail.push(message);
            if (logTail.length > MP3_LOG_TAIL_SIZE) logTail.shift();
            onMessage(message);
        });
    } catch (err) {
        throw fail("load-ffmpeg", "MP3 conversion failed while loading ffmpeg", {cause: err});
    }

    let mountPoint: string | null = null;
    try {
        let inputPath = MP3_INPUT_FILE;
        try {
            const {FFFSType} = await import("@ffmpeg/ffmpeg");
            // WORKERFS reads the blob lazily, so the media never gets copied into the
            // ffmpeg heap. Without it, long videos blow the 32-bit address space.
            await ffmpeg.createDir(MP3_INPUT_DIR);
            await ffmpeg.mount(
                FFFSType.WORKERFS,
                {blobs: [{name: MP3_INPUT_FILE, data: inputBlob}]},
                MP3_INPUT_DIR
            );
            mountPoint = MP3_INPUT_DIR;
            inputPath = `${MP3_INPUT_DIR}/${MP3_INPUT_FILE}`;
        } catch (err) {
            logger.log("toMp3: WORKERFS mount unavailable, falling back to writeFile", err);
            try {
                await ffmpeg.writeFile(MP3_INPUT_FILE, new Uint8Array(await inputBlob.arrayBuffer()));
            } catch (writeErr) {
                throw fail("write-input", "MP3 conversion failed while writing the source into ffmpeg", {
                    cause: writeErr,
                });
            }
        }

        let exitCode: number | undefined;
        try {
            exitCode = await ffmpeg.exec(["-i", inputPath, "-vn", MP3_OUTPUT_FILE]);
        } catch (err) {
            throw fail("convert", "MP3 conversion failed while running ffmpeg", {cause: err});
        }

        // ffmpeg.exec resolves with an exit code instead of throwing, so a non-zero
        // code is the only signal that the conversion actually failed.
        if (typeof exitCode === "number" && exitCode !== 0) {
            throw fail("convert", `MP3 conversion failed: ffmpeg exited with code ${exitCode}`, {exitCode});
        }

        let data: Awaited<ReturnType<typeof ffmpeg.readFile>>;
        try {
            data = await ffmpeg.readFile(MP3_OUTPUT_FILE);
        } catch (err) {
            throw fail("read-output", "MP3 conversion failed while reading the converted audio", {cause: err});
        }

        const outputSize = typeof data === "string" ? data.length : data?.byteLength ?? 0;
        if (!outputSize) {
            throw fail("read-output", "MP3 conversion failed: ffmpeg produced an empty audio file");
        }

        return URL.createObjectURL(new Blob([data as BlobPart], {type: "audio/mp3"}));
    } finally {
        if (mountPoint) {
            try {
                await ffmpeg.unmount(mountPoint);
            } catch (err) {
                logger.log("toMp3: unmount failed", err);
            }
        }
        // Free the wasm heap (and the worker) even when the conversion failed.
        try {
            ffmpeg.terminate();
        } catch (err) {
            logger.log("toMp3: terminate failed", err);
        }
    }
}

const BLOB_FLUSH_BYTES = 32 * 1024 * 1024;

/**
 * Drains a stream into a Blob, flushing to the browser's blob storage as it goes so the
 * whole file is never held as JS heap allocations.
 */
export async function readableStreamToBlob(readable: ReadableStream<Uint8Array>, type?: string) {
    const reader = readable.getReader();
    const parts: BlobPart[] = [];
    let pending: Uint8Array[] = [];
    let pendingLength = 0;

    const flush = () => {
        if (!pending.length) return;
        parts.push(new Blob(pending));
        pending = [];
        pendingLength = 0;
    };

    try {
        while (true) {
            const {value, done} = await reader.read();
            if (done) break;
            if (!value || !value.length) continue;

            pending.push(value);
            pendingLength += value.length;

            if (pendingLength >= BLOB_FLUSH_BYTES) {
                flush();
            }
        }
    } finally {
        reader.releaseLock();
    }

    flush();

    return new Blob(parts, type ? {type} : undefined);
}

export async function readableStreamToUint8Array(readable: ReadableStream<Uint8Array>) {
    const reader = readable.getReader();
    const chunks: Uint8Array[] = [];
    let totalLength = 0;

    while (true) {
        const {value, done} = await reader.read();
        if (done) break;

        chunks.push(value);
        totalLength += value.length;
    }

    const result = new Uint8Array(totalLength);
    let offset = 0;
    for (const chunk of chunks) {
        result.set(chunk, offset);
        offset += chunk.length;
    }

    return result;
}

export function createReadableStream() {
    let controller: ReadableStreamDefaultController<Uint8Array>;
    const readable = new ReadableStream<Uint8Array>({
        start(streamController) {
            controller = streamController;
        }
    });

    return {
        readable,
        write(chunk: Uint8Array) {
            controller.enqueue(chunk);
        },
        close() {
            controller.close();
        },
        error(error: unknown) {
            controller.error(error);
        }
    };
}

export const showConfetti = (config = {}) => {
    confetti({
        zIndex: 9999,
        particleCount: 200,
        spread: 130,
        origin: {y: 0.6},
        ...config
    });
};

export function basename(path) {
    const cleanPath = path.split('?')[0].split('#')[0];
    return cleanPath.split('/').pop();
}

export function parseExtensionVersion(v: string) {
    const [major, minor,patch] = v.split('.')
    return {
        major: +major,
        minor: +minor,
        patch: +patch,
    }
}

export const CHROME_EXTENSION_URL = process.env.NEXT_PUBLIC_PLATFORM === 'twitch'
    ? 'https://chromewebstore.google.com/detail/twitch-vod-downloader/mgabbogigbgahfnceabjddbgpkemhdbl'
    : 'https://chromewebstore.google.com/detail/abllalcelecmcopjgejfbmkhphcmpmae'

export const isProd = process.env.NEXT_PUBLIC_NODE_ENV === "production";
export const isStaging =  process.env.NEXT_PUBLIC_NODE_ENV === "staging";
export const isDev = !isProd && !isStaging


export function wait(ms) {
    return new Promise((resolve) => {
        setTimeout(resolve, ms);
    });
}

export function promptTabClose(e) {
    var confirmationMessage = "You have the video being downloaded. Are you sure you want to leave?";
    (e || window.event).returnValue = confirmationMessage; // Legacy support for older browsers
    return confirmationMessage; // For modern browsers
}

export function storeSavedUrl(url) {

    const DEFAULT_STATE = {
        saved: []
    }
    let state = null;
    try {
        state = JSON.parse(localStorage.getItem("kvd"))
    } catch (err) {
    }
    if (!state) {
        state = DEFAULT_STATE
    }
    if (!state.saved) {
        state.saved = []
    }
    state.saved.push({
        url,
        timestamp: Date.now()
    })
    localStorage.setItem("kvd", JSON.stringify(state));
    return state
}

export function getQueryParam(url, param) {
    var urlParams = new URLSearchParams(new URL(url).search);
    return urlParams.get(param);
}

export function calculateVideoSize2(sizeInBytes) {
    let sizeInKB = sizeInBytes / 1000;
    let sizeInMB = sizeInKB / 1000;
    let sizeInGB = sizeInMB / 1000;

    if (sizeInBytes < 1024) {
        return sizeInBytes.toFixed(2) + ' Bytes';
    } else if (sizeInKB < 1024) {
        return sizeInKB.toFixed(2) + ' KB';
    } else if (sizeInMB < 1024) {
        return sizeInMB.toFixed(2) + ' MB';
    } else {
        return sizeInGB.toFixed(2) + ' GB';
    }
}

export function slugify(text) {
    return text
        .toString()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim()
        .replace(/\s+/g, '-')
        .replace(/[^\w-]+/g, '')
        .replace(/--+/g, '-')
}
export function removeQueryParam(paramKey) {
    // Create a URL object from the current window location
    const url = new URL(window.location.href);

    // Use URLSearchParams to delete the specified parameter
    url.searchParams.delete(paramKey);

    // Update the URL in the browser's history without reloading the page
    window.history.replaceState(null, '', url.toString());
}

export async function hasAdBlocker() {
    return new Promise((resolve) => {
        const test = document.createElement("script");
        test.src = "/ads.js"; // fake ad-like URL
        test.onerror = () => resolve(true);
        test.onload = () => resolve(false);
        document.body.appendChild(test);
    })
}



export function cacheWrapper(fn) {
    const cache = new Map();

    return function (...args) {
        const key = JSON.stringify(args);
        if (cache.has(key)) {
            return cache.get(key);
        }

        const result = Promise.resolve(fn(...args)).catch(err => {
            cache.delete(key);
            throw err;
        });

        cache.set(key, result);
        return result;
    };
}

export function _fetchText(txt) {
    return fetch(txt).then((a) => a.text());
}

export function hlsParse(txt) {
    const pl = parse(txt);
    if(pl.segments && pl.segments.length) {
        pl.segments = pl.segments.filter(segment => segment.uri)
    }
    return pl
}

export async function _fetchTextWithHeaders(url) {
    const resp = await fetch(url);
    return [
        await resp.text(),
        parseResponseHeaders(resp)
    ];
}

function parseResponseHeaders(response) {
    const headersObj = {};
    for (const [key, value] of response.headers.entries()) {
        headersObj[key] = value;
    }
    return headersObj;
}

export const fetchText = cacheWrapper(_fetchText)

export function once(fn) {
    let called = false;
    let result;
    return function (...args) {
        if (!called) {
            called = true;
            result = fn.apply(this, args);
        }
        return result;
    };
}

export function getPreferredExt() {
    try {
        if ([".mp4", ".ts"].includes(localStorage.ext)) {
            return localStorage.ext
        }
    } catch (err) {
    }
    return ".mp4"
}

export interface FilePromptResult {
    writableStream: ReturnType<typeof createReadableStream> | FileSystemWritableFileStream | WritableStreamDefaultWriter<Uint8Array>;
    saveInfo: DownloadSaveInfo;
    mp3FileHandle?: {
        createWritable: () => Promise<FileSystemWritableFileStream>;
        // Chromium-only, non-standard.
        remove?: () => Promise<void>;
    };
}

export async function writeFilePrompt(suggestedName: string, ext = '.mp4', show?): Promise<FilePromptResult | null> {
    const fileName = suggestedName.toLowerCase().endsWith(ext.toLowerCase())
        ? suggestedName
        : suggestedName + ext;

    if ('showSaveFilePicker' in window) {
        try {
            const showSaveFilePicker = window.showSaveFilePicker as (options: {
                id?: string;
                startIn?: string;
                suggestedName: string;
                types: Array<{accept: Record<string, string[]>}>;
            }) => Promise<{
                name: string;
                createWritable: () => Promise<FileSystemWritableFileStream>;
                remove?: () => Promise<void>;
            }>;
            const pickerOptions: Parameters<typeof showSaveFilePicker>[0] = {
                id: "video-download",
                suggestedName: fileName,
                types: [
                    {
                        accept: ext === '.vtt'
                            ? {"text/vtt": [".vtt"]}
                            : ext === '.mp3' ? {"audio/mpeg": [".mp3"]}
                            : ext === '.ts' ? {"video/mp2t": [".ts"]} : {'video/mp4': ['.mp4']},
                    },
                ],
            };
            let handle: Awaited<ReturnType<typeof showSaveFilePicker>>;
            try {
                handle = await showSaveFilePicker({startIn: "downloads", ...pickerOptions});
            } catch (err) {
                if (!(err instanceof Error) || err.name !== 'NotFoundError') throw err;
                logger.log("Save picker could not resolve Downloads, retrying without startIn", err);
                try {
                    handle = await showSaveFilePicker(pickerOptions);
                } catch (retryErr) {
                    if (retryErr instanceof Error && retryErr.name === 'AbortError') throw retryErr;
                    logger.log("Save picker retry failed, falling back to Downloads", retryErr);
                    return await createDownloadsWriter(suggestedName, ext);
                }
            }
            if (ext === '.mp3') {
                return {
                    writableStream: createReadableStream(),
                    mp3FileHandle: handle,
                    saveInfo: {
                        location: "chosen-location",
                        fileName: handle.name,
                    },
                };
            }
            return {
                writableStream: await handle.createWritable(),
                saveInfo: {
                    location: "chosen-location",
                    fileName: handle.name,
                } satisfies DownloadSaveInfo,
            };
        } catch (err) {
            const errorName = err instanceof Error ? err.name : undefined;
            console.log('writeFilePrompt ERROR', {err, errorName})
            if (errorName === 'AbortError') return null;

            // Browser exposes the picker but blocks writing (Brave shields,
            // cross-origin iframe, some webviews) -> fall back to StreamSaver.
            if (errorName === 'NotAllowedError') {
                console.log('err.name: NotAllowedError', errorName)
                return await createDownloadsWriter(suggestedName, ext);
            }

            if (errorName === 'NotFoundError') {
                logger.log("Could not create the selected file, falling back to Downloads", err);
                return await createDownloadsWriter(suggestedName, ext);
            }

            const errMessage = err instanceof Error ? err.message : String(err)
            const isTooLateIssue = errMessage.includes("Must be handling a user gesture to show a file picker")
            logger.log('catched ERRR!', isTooLateIssue)
            if (isTooLateIssue && show) {
                await show({
                    component: ChooseFileLocationPopup,
                    data: {suggestedName, ext},
                });

                return writeFilePrompt(suggestedName, ext, show);
            }
            throw err
        }
    } else {
        return await createDownloadsWriter(suggestedName, ext)
    }
}

async function createDownloadsWriter(suggestedName: string, ext: string): Promise<FilePromptResult> {
    const baseName = suggestedName.toLowerCase().endsWith(ext.toLowerCase())
        ? suggestedName.slice(0, -ext.length)
        : suggestedName;
    if (ext === '.mp3') {
        return {
            writableStream: createReadableStream(),
            saveInfo: {location: "downloads", fileName: baseName + ext},
        };
    }
    const fileName = baseName + `-${Date.now()}` + ext;
    // StreamSaver can reject with no value on browsers with no usable save picker
    // (Android browsers, Brave). Rethrow an identifiable error so the toast names it.
    const writableStream = await getCustomStreams(fileName).catch(err => {
        throw new StreamSaverError(undefined, err);
    });
    return {
        writableStream,
        saveInfo: {location: "downloads", fileName} satisfies DownloadSaveInfo,
    };
}

export async function saveMp3Download(url: string, {mp3FileHandle, saveInfo}: FilePromptResult) {
    if (!mp3FileHandle) {
        triggerDownload(url, saveInfo.fileName);
        return;
    }

    try {
        const response = await fetch(url);
        if (!response.ok) {
            throw new Error(`Could not read the converted audio (${response.status})`);
        }
        const blob = await response.blob();
        const writer = await mp3FileHandle.createWritable();
        try {
            await writer.write(blob);
            await writer.close();
        } catch (err) {
            try {
                await writer.abort(err);
            } catch (abortErr) {
                logger.log("Could not abort the MP3 file writer", abortErr);
            }
            throw err;
        }
    } finally {
        URL.revokeObjectURL(url);
    }
}

// The save picker creates (or truncates) the file up front, but MP3 bytes are only
// written after conversion, so a failed MP3 flow would leave an empty file behind.
export async function discardMp3File({mp3FileHandle}: FilePromptResult) {
    if (!mp3FileHandle?.remove) return;
    try {
        await mp3FileHandle.remove();
    } catch (err) {
        logger.log("Could not remove the empty MP3 file", err);
    }
}

async function getCustomStreams(fileName: string) {
    const streamSaver = await import("streamsaver")
    const fileStream = await streamSaver.createWriteStream(fileName || 'cat2.mp4')

    const writer = await fileStream.getWriter()
    return writer;
}

export async function checkStorageUsage() {
    if (navigator.storage && navigator.storage.estimate) {
        return navigator.storage.estimate().then(estimate => {
            if (isDev) {
                logger.log(`Usage: ${estimate.usage}`);
                logger.log(`Quota: ${estimate.quota}`);
                logger.log(`Usage Percentage: ${(estimate.usage / estimate.quota) * 100}%`);
            }
            return estimate.quota - estimate.usage
        });
    } else {
        logger.log('Storage API not supported.');
    }
}

export function getExtensionVersion(extension): number {
    return +((extension?.version || "").replaceAll(".", ""));
}

type Success<T> = [data: T, error: null];

type Failure<E> = [data: null, error: E];

type Result<T, E = Error> = Success<T> | Failure<E>;

// Main wrapper function
export async function tryCatch<T, E = Error>(promise: Promise<T>): Promise<Result<T, E>> {
    try {
        const data = await promise;
        return [data, null];
    } catch (error) {
        return [null, error as E];
    }
}

export function triggerDownload(url: string, filename?: string) {
    setTimeout(() => {
        const a = document.createElement("a");
        a.href = url;
        if(filename) {
            a.download = filename;
        }
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    }, 10);
}

// getCustomStreams().then(async writer => {
//     const url = 'https://d8d913s460fub.cloudfront.net/videoserver/cat-test-video-320x240.mp4'
//     const data =await downloadVideo(url)
//     await writer.write(data)
//     await writer.close()
// })

// (async () => {
//     const response = await fetch("https://stream.kick.com/ivs/v1/196233775518/gb20mPcO74wk/2024/4/20/18/4/jlgD0cgg6Gqa/media/hls/1080p60/4.ts")
//     const buffer = await response.arrayBuffer();
//     const data = new Uint8Array(buffer);
//     const writableStream = await getCustomStreams("-kick-com.ts")
//     for (let i = 0; i < 200; i++) {
//         await writableStream.write(data)
//     }
//     writableStream.close()
// })()

// useEffect(() => {
//     if (typeof window !== 'undefined' && !!getQueryParam(window.location.href, "sw")) {
//         navigator.serviceWorker
//             .register('/service-worker.js', {type: "module"})
//             .then((registration) => {
//                 logger.log(registration);
//             })
//     }
// }, []);
